import { useMemo } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { DAYS, colorOf, toMin, fmtRange, fmtTime, todayKey, sessionsForDay, dayShort, GOLD } from "../../lib/content";
import { useNow } from "../../hooks/useNow";
import { useLang } from "../../lib/i18n";

const HOUR_H = 76;

const layoutDay = (items) => {
  const out = [];
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const lanes = [];
    cluster.forEach((it) => {
      let lane = lanes.findIndex((end) => end <= it.a);
      if (lane === -1) { lane = lanes.length; lanes.push(it.b); } else lanes[lane] = it.b;
      it.lane = lane;
    });
    cluster.forEach((it) => out.push({ ...it, lanes: lanes.length }));
    cluster = [];
  };
  items.forEach((it) => {
    if (it.a >= clusterEnd && cluster.length) flush();
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.b);
  });
  if (cluster.length) flush();
  return out;
};

export const edge = (lang, c) => `inset ${lang === "ar" ? -3 : 3}px 0 0 ${c}`;

const GAP_H = 34;

// Time scale that collapses empty stretches of 2+ hours into a thin band
const buildScale = (ranges, sh, eh) => {
  const busy = (h) => ranges.some(([a, b]) => a < (h + 1) * 60 && b > h * 60);
  const segs = [];
  for (let h = sh; h < eh; ) {
    if (busy(h)) { segs.push({ from: h, to: h + 1 }); h += 1; continue; }
    let e = h;
    while (e < eh && !busy(e)) e += 1;
    if (e - h >= 2) segs.push({ from: h, to: e, gap: true });
    else for (let k = h; k < e; k += 1) segs.push({ from: k, to: k + 1 });
    h = e;
  }
  let off = 0;
  segs.forEach((s) => { s.top = off; s.h = s.gap ? GAP_H : HOUR_H; off += s.h; });
  const y = (min) => {
    const s = segs.find((x) => min < x.to * 60) || segs[segs.length - 1];
    const p = Math.min(Math.max((min - s.from * 60) / ((s.to - s.from) * 60), 0), 1);
    return s.top + p * s.h;
  };
  return { segs, total: off, y };
};

const Block = ({ it, y, onOpen }) => {
  const { lang, pick } = useLang();
  const c = colorOf(it.cls.color);
  const spec = it.cls.program !== "dillusions";
  const tone = spec ? GOLD : c;
  const top = y(it.a);
  const h = y(it.b) - top;
  const w = 100 / it.lanes;
  return (
    <motion.button
      onClick={() => onOpen(it.cls)}
      data-testid={`schedule-class-card-${it.cls.id}-${it.s.day.toLowerCase()}`}
      initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.7 + it.idx * 0.05 }}
      whileHover={{ y: -2 }}
      className={`group absolute flex flex-col justify-start overflow-hidden rounded-lg p-2 text-start ${spec ? "border-[1.5px]" : "border"}`}
      style={{ top: top + 2, height: h - 4, insetInlineStart: `calc(${it.lane * w}% + 3px)`, width: `calc(${w}% - 6px)`,
        background: spec ? "rgba(212,175,55,0.05)" : `${c}30`, borderColor: spec ? GOLD : `${c}66`, boxShadow: spec ? "none" : edge(lang, c) }}>
      <span className="flex items-center gap-1 truncate font-mono text-[11px] font-medium" style={{ color: tone }}>
        {spec && <Star size={11} fill={GOLD} strokeWidth={0} className="shrink-0" data-testid="specialty-star" />}
        <span className="truncate">{it.lanes > 1 ? fmtTime(it.s.start, true, lang) : fmtRange(it.s, lang)}</span>
      </span>
      <span className={`mt-0.5 block font-display font-bold leading-tight text-white ${it.lanes > 1 ? "text-[13px] break-words" : "truncate text-[14px]"}`}>{pick(it.cls, "title")}</span>
      <span className="block truncate text-[12px] text-zinc-300">{it.cls.program === "dillusions" ? pick(it.cls, "tag") : [pick(it.cls, "style"), pick(it.cls, "ages")].filter(Boolean).join(" · ")}</span>
      <span className="pointer-events-none absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100" style={{ boxShadow: `0 0 24px ${tone}55 inset` }} />
    </motion.button>
  );
};

export const WeekGrid = ({ classes, onOpen }) => {
  const now = useNow();
  const today = todayKey();
  const { t, lang, pick } = useLang();

  const { scale, byDay, tba, unscheduled } = useMemo(() => {
    const timed = classes.flatMap((c) => c.sessions.filter((s) => toMin(s.start) !== null));
    const ranges = timed.map((s) => [toMin(s.start), toMin(s.end) ?? toMin(s.start) + 60]);
    const sh = ranges.length ? Math.floor(Math.min(...ranges.map((r) => r[0])) / 60) : 17;
    const eh = ranges.length ? Math.ceil(Math.max(...ranges.map((r) => r[1])) / 60) : 21;
    let idx = 0;
    const bd = {};
    const tb = {};
    DAYS.forEach((d) => {
      const list = sessionsForDay(classes, d);
      bd[d] = layoutDay(list.filter((x) => toMin(x.s.start) !== null)
        .map((x) => ({ ...x, a: toMin(x.s.start), b: toMin(x.s.end) ?? toMin(x.s.start) + 60, idx: idx++ })));
      tb[d] = list.filter((x) => toMin(x.s.start) === null);
    });
    return { scale: buildScale(ranges, sh, Math.max(eh, sh + 1)), byDay: bd, tba: tb,
      unscheduled: classes.filter((c) => c.sessions.length === 0) };
  }, [classes]);

  const hasTba = Object.values(tba).some((l) => l.length);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const { segs, total, y } = scale;
  const nowSeg = segs.find((s) => nowMin >= s.from * 60 && nowMin < s.to * 60);
  const showNow = !!nowSeg && !nowSeg.gap;
  const nowTop = showNow ? y(nowMin) : 0;
  const hourLabel = (h) => fmtTime(`${h}:00`, true, lang).replace(":00", "");
  const Lines = () => segs.map((s) => (
    <div key={s.from} aria-hidden="true" className={`absolute inset-x-0 border-t border-white/[0.06] ${s.gap ? "gap-band" : ""}`} style={{ top: s.top, height: s.h }} />
  ));

  return (
    <div className="timetable-card rounded-2xl border border-white/10 bg-[#101017]/85 backdrop-blur-xl" data-testid="week-grid">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/[0.07] px-6 py-4">
        <div className="flex items-baseline gap-4">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white">{t.weekly}</h2>
          <span className="font-mono text-[12px] uppercase tracking-[0.18em] text-zinc-400">{t.tapHint}</span>
        </div>
      </div>

      <div className="grid grid-cols-[64px_repeat(7,minmax(0,1fr))]">
        <div className="border-b border-white/[0.07]" />
        {DAYS.map((d) => (
          <div key={d} className={`border-b border-l border-white/[0.07] px-3 py-3 ${d === today ? "bg-accent/[0.06]" : ""}`} data-testid={`grid-day-header-${d.toLowerCase()}`}>
            <span className={`font-mono text-[12px] uppercase tracking-[0.2em] ${d === today ? "text-accent" : "text-zinc-400"}`}>{dayShort(d, lang)}</span>
            {d === today && <span className="ms-2 rounded-full bg-accent px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-onaccent">{t.today}</span>}
            <span className="block text-[12px] text-zinc-500">{t.classCount(byDay[d].length + tba[d].length)}</span>
          </div>
        ))}

        <div className="relative" style={{ height: total }}>
          {segs.map((s, i) => (s.gap ? (
            <span key={s.from} className="absolute inset-x-1 flex items-center justify-center text-center font-mono text-[10px] leading-tight text-zinc-500" style={{ top: s.top, height: s.h }} data-testid="grid-gap-label">
              {hourLabel(s.from)}–{hourLabel(s.to)}
            </span>
          ) : (
            <span key={s.from} className="absolute end-3 -translate-y-1/2 font-mono text-[11px] text-zinc-400" style={{ top: s.top + (i === 0 ? 8 : 0) }}>
              {segs[i - 1]?.gap ? "" : hourLabel(s.from)}
            </span>
          )))}
          <span className="absolute end-3 -translate-y-1/2 font-mono text-[11px] text-zinc-400" style={{ top: total }}>{hourLabel(segs[segs.length - 1].to)}</span>
        </div>
        {DAYS.map((d) => (
          <div key={d} className={`relative border-l border-white/[0.07] ${d === today ? "bg-accent/[0.03]" : ""} ${!byDay[d].length && !tba[d].length ? "rest-day" : ""}`}
            style={{ height: total }}>
            <Lines />
            {!byDay[d].length && !tba[d].length && (
              <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{t.restDay}</span>
            )}
            {byDay[d].map((it) => <Block key={`${it.cls.id}-${it.s.start}`} it={it} y={y} onOpen={onOpen} />)}
            {d === today && showNow && (
              <div className="pointer-events-none absolute inset-x-0 z-10 flex items-center" style={{ top: nowTop }} data-testid="grid-now-line">
                <span className="h-2 w-2 rounded-full bg-accent2" />
                <span className="h-px flex-1 bg-accent2" />
              </div>
            )}
          </div>
        ))}

        {hasTba && (
          <>
            <div className="flex items-center justify-end border-t border-white/[0.07] pe-3 font-mono text-[11px] text-zinc-400">{t.tba}</div>
            {DAYS.map((d) => (
              <div key={d} className="min-h-[56px] border-l border-t border-white/[0.07] p-1.5">
                {tba[d].map(({ cls }) => (
                  <button key={cls.id} onClick={() => onOpen(cls)} data-testid={`schedule-class-card-${cls.id}-${d.toLowerCase()}-tba`}
                    className="w-full rounded-lg border border-dashed p-2 text-start"
                    style={{ borderColor: `${colorOf(cls.color)}88` }}>
                    <span className="block font-mono text-[11px]" style={{ color: colorOf(cls.color) }}>{t.timeTba}</span>
                    <span className="block truncate font-display text-[13px] font-bold text-white">{pick(cls, "title")} · {pick(cls, "style")}</span>
                  </button>
                ))}
              </div>
            ))}
          </>
        )}
      </div>

      {unscheduled.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 border-t border-white/[0.07] px-6 py-3" data-testid="grid-unscheduled">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{t.comingSoon}</span>
          {unscheduled.map((c) => (
            <button key={c.id} onClick={() => onOpen(c)} data-testid={`schedule-class-card-${c.id}`}
              className="rounded-full border border-dashed px-3 py-1 text-[13px] text-white transition-colors hover:bg-white/5" style={{ borderColor: `${colorOf(c.color)}88` }}>
              {t.withName(pick(c, "style"), pick(c, "title"))} — <span className="text-zinc-400">{t.scheduleTba}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
